import { z } from "zod";

/**
 * Why a batch of gift codes was generated: a fixed label for reporting, since every type redeems the
 * same way (boxed's `GiftCodeType`, without `PAID`: a redemption here is never funding).
 */
export const GIFT_CODE_TYPES = ["giveaway", "influencer", "support", "vip", "incentive"] as const;
export type GiftCodeType = (typeof GIFT_CODE_TYPES)[number];

export const GIFT_CODE_TYPE_LABELS: Record<GiftCodeType, string> = {
  giveaway: "Giveaway",
  influencer: "Influencer",
  support: "Support",
  vip: "VIP",
  incentive: "Incentive",
};

/**
 * A code's state, worked out on read: `redeemed` once a shopper redeemed it, else `voided` once
 * staff voided it or its batch, else `expired` once its batch's expiry passed, else `active`.
 */
export const GIFT_CODE_STATUSES = ["active", "redeemed", "expired", "voided"] as const;
export type GiftCodeStatus = (typeof GIFT_CODE_STATUSES)[number];

/**
 * One member per refusal cause of `gift_code_not_redeemable`, carried in `details.reason`:
 * `unknown` is no such code in the environment (a mistyped one included), and the other three are
 * the code's own status.
 */
export const GIFT_CODE_REDEMPTION_ISSUES = ["unknown", "redeemed", "expired", "voided"] as const;
export type GiftCodeRedemptionIssue = (typeof GIFT_CODE_REDEMPTION_ISSUES)[number];

/** The most codes one batch holds (boxed parity). */
export const GIFT_CODE_BATCH_MAX = 1_000;

/** The most one code may be worth, in cents (boxed parity: 10,000 dollars). */
export const GIFT_CODE_MAX_AMOUNT_CENTS = 1_000_000;

/** POST /v1/me/gift-codes/redeem. Dashes, spaces and case are ignored. */
export const redeemGiftCodeRequestSchema = z.object({ code: z.string().trim().min(1).max(64) });
export type RedeemGiftCodeRequest = z.infer<typeof redeemGiftCodeRequestSchema>;
